import {
  Button,
  Card,
  Row,
  Select,
  Tooltip,
  Descriptions,
  Tag,
  Space,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { ColumnComponent } from "../../components/ui";
import Table from "antd/es/table";
import DataContext from "../../context/DataContext";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { SearchInput } from "../../components/functional";
import {
  CustomerProps,
  ProjectProps,
  ProjectTemplateProps,
} from "../../interfaces/interfaces";
import { exportCustomerFile } from "../../utils/func/ExportFile";
import { CustomerDrawer, CustomerTable } from "../../components/customer";
import dayjs from "dayjs";
import { useDemoModal } from "../../hooks";
import { DrawerCreateOrUpdateProject } from "../../components/project";
import { filterOption, filterOptionGroup } from "../../utils/func/utils";

type ProjectInfoDisplayProps = {
  project: ProjectProps | null;
  idClient: string;
  onSaveProject: (filters: any) => void;
};

const ProjectInfoDisplay: React.FC<ProjectInfoDisplayProps> = ({
  project,
  idClient,
  onSaveProject,
}) => {
  const {
    projectModal,
    handleChangeProjectSelected,
    openItemModal,
    closeItemModal,
    handleEditData,
    getData,
    getDataById,
    handleChangeProjectModalStatus,
    templateSelected,
  } = useContext(DataContext);
  const { isDemoEnv, getClientInformation } = useContext(AuthContext);
  const { DemoModal, openModalDemo } = useDemoModal();

  if (!project) return null;

  const createOrUpdateProject = (
    values: ProjectProps,
    idClient: string,
    idProject?: string,
  ) => {
    if (projectModal?.update && idProject) {
      handleEditData({
        endpoint: `project/${idClient}/${idProject}`,
        newData: values,
        next: () => {
          idClient &&
            idProject &&
            onSaveProject({
              active: true,
              project: idProject,
            });
          getData("project-templates", idClient);
          handleChangeProjectSelected(null);
          templateSelected &&
            templateSelected._id &&
            getDataById({
              type: "project-template",
              endpoint: `project-template/${idClient}/${templateSelected._id}`,
              showAlert: false,
            });
          closeItemModal("project");
        },
      });
    }
  };

  return (
    <>
      <Card
        title="Project Information"
        bordered={false}
        hoverable={false}
        className="summary-client__card"
        extra={
          <Tooltip title="Edit Project">
            <Button
              icon={<EditOutlined />}
              onClick={() => openItemModal("project", false, project)}
            ></Button>
          </Tooltip>
        }
      >
        <Descriptions column={1} size="middle" layout="horizontal">
          <Descriptions.Item label="Name">{project.name}</Descriptions.Item>
          <Descriptions.Item label="Description">
            {project.description}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {project.status?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            {project.active ? (
              <Tag color="green">Active</Tag>
            ) : (
              <Tag color="red">Inactive</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Start Date">
            {project.startDate
              ? dayjs(project.startDate).format("MM/DD/YYYY")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="End Date">
            {project.endDate
              ? dayjs(project.endDate).format("MM/DD/YYYY")
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Assigned To">
            {Array.isArray(project.assignedTo) ? (
              project.assignedTo?.map((assigned) => (
                <Tag
                  color="blue"
                  key={assigned._id}
                  style={{
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {assigned?.name}
                </Tag>
              ))
            ) : (
              <div
                style={{
                  maxWidth: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {project.assignedTo?.name}
              </div>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Location">
            {project.location?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Tags">
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {project.tags && project.tags.length > 0
                ? project.tags.map((tag) => (
                    <Tag
                      key={tag._id}
                      color={tag.color}
                      style={{ marginBottom: 4 }}
                    >
                      {tag.name}
                    </Tag>
                  ))
                : "None"}
            </div>
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <DrawerCreateOrUpdateProject
        modalStatus={projectModal}
        idClient={idClient}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (idClient) {
              createOrUpdateProject(values, idClient, values._id);
            }
          }
        }}
        handleChangeModalStatus={(payload) =>
          handleChangeProjectModalStatus({
            ...projectModal,
            payload: payload || projectModal?.payload,
            create: false,
            update: true,
          } as any)
        }
        handleCreateSource={(source) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newSource: source,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        handleCreateLocation={(locationName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newLocation: locationName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
          });
        }}
        handleCreateNewProjectStatus={(projectStatusName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newProjectStatus: projectStatusName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        onCancel={() => {
          closeItemModal("project");
        }}
      />
      <DemoModal />
    </>
  );
};

export const CustomerListByProject = () => {
  const { idClient, idProject } = useParams();
  const navigate = useNavigate();
  const {
    projectInformation,
    templateSelected,
    projectTemplateList,
    projectsByTemplate,
    projectList,
    projectSelected,
    handleChangeTemplateSelected,
    getData: getListData,
    getDataById,
    handleChangeProjectSelected,
    getCustomersListByProject,
    getCustomersListByClientPaginated,
  } = useContext(DataContext);
  const [selectedRowsToEdit, setSelectedRowToEdit] = useState<React.Key[]>([]);
  const [customerDrawerStatus, setCustomerDrawerStatus] = useState<{
    open: boolean;
    idCustomer: string | null;
  }>({ open: false, idCustomer: "" });
  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    if (idClient) {
      getListData("customer", idClient);
      getListData("projects", idClient, undefined, { active: true });
      getListData("users", idClient);
      getListData("project-templates", idClient, undefined, {
        active: true,
      });
      getCustomersListByClientPaginated({
        idClient,
        filters: {
          active: true,
        },
      });
    }
  }, [idClient]);

  useEffect(() => {
    if (idClient && idProject && idProject !== "null") {
      getCustomersListByProject({ idClient, idProject });

      getCustomersListByClientPaginated({
        idClient,
        filters: {
          active: true,
          project: idProject,
        },
      });
      handleChangeProjectSelected(idProject);
    }
  }, [idClient, idProject]);

  const filteredCustomers = projectInformation.customers?.filter(
    (customer: CustomerProps) =>
      customer.DisplayName?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const onChangeSelectedRowsToEdit = (newSelectedRowToEdit: React.Key[]) => {
    setSelectedRowToEdit(newSelectedRowToEdit);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowsToEdit,
    onChange: onChangeSelectedRowsToEdit,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <Card
            className="summary-client__card"
            styles={{ body: { display: "none" } }}
            actions={[
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Templates</label>
                <Tooltip title="Filter By Template">
                  <Select
                    filterOption={filterOption}
                    showSearch
                    size="middle"
                    allowClear
                    placeholder="Templates"
                    className="summary-client__card__actions__select"
                    options={projectTemplateList.map(
                      (template: ProjectTemplateProps) => ({
                        label: template.name,
                        value: template._id,
                      }),
                    )}
                    value={templateSelected?._id}
                    onChange={(value) => {
                      if (value) {
                        getDataById({
                          type: "project-template",
                          endpoint: `project-template/${idClient}/${value}`,
                          showAlert: false,
                        });
                        handleChangeTemplateSelected(
                          projectTemplateList.find(
                            (template: ProjectTemplateProps) =>
                              template._id === value,
                          ) || null,
                        );
                      } else {
                        handleChangeTemplateSelected(null);
                      }
                    }}
                  />
                </Tooltip>
              </Space>,
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Projects</label>
                <Tooltip title="Filter By Project">
                  <Select
                    filterOption={filterOptionGroup}
                    showSearch
                    size="middle"
                    placeholder="Projects"
                    className="summary-client__card__actions__select"
                    value={projectSelected}
                    onSelect={(value) => {
                      idClient &&
                        navigate(`/customer-project/${idClient}/${value}`);
                      handleChangeProjectSelected(value);
                      idClient &&
                        getCustomersListByClientPaginated({
                          idClient,
                          filters: {
                            active: true,
                            project: value,
                          },
                        });
                    }}
                  >
                    {templateSelected && (
                      <Select.OptGroup label="Projects by Template">
                        {projectsByTemplate.map((s) => (
                          <Select.Option key={s._id} value={s._id}>
                            {s.name}
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    )}
                    <Select.OptGroup label="Projects">
                      {projectList.map((s) => (
                        <Select.Option key={s._id} value={s._id}>
                          {s.name}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  </Select>
                </Tooltip>
              </Space>,
              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Customers</label>

                <SearchInput
                  placeholder="Search Customer"
                  onSearch={(value: string) => {
                    if (value) {
                      setSearchText(value);

                      idClient &&
                        getCustomersListByClientPaginated({
                          idClient,
                          filters: {
                            active: true,
                            project: idProject,
                          },
                          search: value,
                        });
                    } else {
                      idClient &&
                        getCustomersListByClientPaginated({
                          idClient,
                          filters: {
                            active: true,
                            project: idProject,
                          },
                          search: undefined,
                        });
                    }
                  }}
                />
              </Space>,

              <Space direction="vertical" size={0} style={{ width: "100%" }}>
                <label>Actions</label>
                <Tooltip
                  title={
                    selectedRowsToEdit.length > 0
                      ? "Export Selected Data"
                      : "Export Data"
                  }
                  placement="left"
                >
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    size="large"
                    onClick={() => {
                      exportCustomerFile(
                        projectInformation.customers as CustomerProps[],
                        selectedRowsToEdit as string[],
                      );
                    }}
                  />
                </Tooltip>
              </Space>,
            ]}
          />
        </ColumnComponent>

        <ColumnComponent xl={12}>
          <ProjectInfoDisplay
            project={projectInformation}
            idClient={idClient || ""}
            onSaveProject={(filters) =>
              idClient &&
              getCustomersListByClientPaginated({
                idClient,
                filters: {
                  active: true,
                  ...filters,
                },
                search: undefined,
              })
            }
          />
        </ColumnComponent>

        <ColumnComponent xl={12}>
          <CustomerTable
            idClient={idClient || ""}
            customerByProjectView={true}
            onSelectOne={(record: CustomerProps) =>
              setCustomerDrawerStatus({ open: true, idCustomer: record._id })
            }
            rowSelection={rowSelection}
            onChangePage={(page, pageSize) =>
              idClient &&
              getCustomersListByClientPaginated({
                idClient,
                filters: {
                  active: true,
                  project: idProject,
                  page: page,
                  perPage: pageSize,
                },
              })
            }
          />
        </ColumnComponent>
      </Row>

      {idClient && (
        <CustomerDrawer
          open={customerDrawerStatus.open}
          idClient={idClient}
          idCustomer={customerDrawerStatus.idCustomer}
          onClose={() =>
            setCustomerDrawerStatus({ open: false, idCustomer: "" })
          }
          onSelectOne={(record: CustomerProps) =>
            setCustomerDrawerStatus({ open: true, idCustomer: record.Id })
          }
          projectPreselected={projectSelected}
        />
      )}
    </>
  );
};
